/* =========================================================
   Viking Signs — Countdown Banner
   File: assets/countdown-banner.js
   ========================================================= */

(function () {
  'use strict';

  // ── Read config injected by the Liquid section ──────────────────────────────
  const cfg = window.vikingCountdownConfig || {};

  const CUTOFF_HOUR   = cfg.cutoffHour   ?? 16;
  const CUTOFF_MINUTE = cfg.cutoffMinute ?? 45;
  const CLOSED_DAYS   = Array.isArray(cfg.closedDays)
    ? cfg.closedDays.map(d => d.trim()).filter(Boolean)
    : [];
  const SHOW_COMPETITOR_NOTE = cfg.competitorNote !== false;
  const COMPETITOR_TEXT = cfg.competitorText || "we ship until 4:45pm, later than most!";

  // ── Element references ───────────────────────────────────────────────────────
  const digitsEl      = document.getElementById('vs-digits');
  const timerEl       = document.getElementById('vs-countdown');
  const messageEl     = document.getElementById('vs-countdown-message');
  const dispatchDateEl= document.getElementById('vs-dispatch-date');
  const deliveryDateEl= document.getElementById('vs-delivery-date');

  if (!digitsEl) return; // Section not present on this page

  // ── Helpers ──────────────────────────────────────────────────────────────────

  /** Returns "YYYY-MM-DD" in local time (avoids UTC midnight offset bugs). */
  function toDateString(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  function isWeekend(date) {
    const day = date.getDay();
    return day === 0 || day === 6;
  }

  function isClosedDay(date) {
    return CLOSED_DAYS.includes(toDateString(date));
  }

  function isWorkingDay(date) {
    return !isWeekend(date) && !isClosedDay(date);
  }

  /**
   * Returns the next working day on or after startDate.
   * Does NOT mutate the input.
   */
  function getNextWorkingDay(startDate) {
    const date = new Date(startDate);
    while (!isWorkingDay(date)) {
      date.setDate(date.getDate() + 1);
    }
    return date;
  }

  /**
   * Returns the next cutoff Date:
   * - Today's cutoff if it's a working day and we're still before cutoff.
   * - Otherwise the next working day's cutoff.
   */
  function getNextCutoff() {
    const now = new Date();

    const todayCutoff = new Date(now);
    todayCutoff.setHours(CUTOFF_HOUR, CUTOFF_MINUTE, 0, 0);

    if (isWorkingDay(now) && now < todayCutoff) {
      return todayCutoff;
    }

    // Move to tomorrow (clone to avoid mutating now)
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);

    const nextWorking = getNextWorkingDay(tomorrow);
    nextWorking.setHours(CUTOFF_HOUR, CUTOFF_MINUTE, 0, 0);
    return nextWorking;
  }

  /**
   * Returns the estimated delivery date: the next working day after dispatchDate.
   */
  function getDeliveryDate(dispatchDate) {
    const day = new Date(dispatchDate);
    day.setDate(day.getDate() + 1);
    day.setHours(0, 0, 0, 0);
    return getNextWorkingDay(day);
  }

  function formatDate(date) {
    return date.toLocaleDateString('en-GB', {
      weekday: 'long',
      day:     'numeric',
      month:   'long',
    });
  }

  // ── Urgency state ────────────────────────────────────────────────────────────

  function applyUrgency(timeDiff) {
    if (!timerEl) return;
    if (timeDiff <= 15 * 60 * 1000) {
      timerEl.dataset.urgency = 'critical';
    } else if (timeDiff <= 60 * 60 * 1000) {
      timerEl.dataset.urgency = 'high';
    } else {
      timerEl.dataset.urgency = '';
    }
  }

  // ── Main update ──────────────────────────────────────────────────────────────

  function updateCountdown() {
    const now    = new Date();
    const cutoff = getNextCutoff();
    const timeDiff = cutoff - now;

    if (timeDiff <= 0) return; // Will recalculate next tick

    // Countdown digits
    const totalSeconds = Math.floor(timeDiff / 1000);
    const h = String(Math.floor(totalSeconds / 3600)).padStart(2, '0');
    const m = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0');
    const s = String(totalSeconds % 60).padStart(2, '0');
    if (digitsEl) digitsEl.textContent = `${h}:${m}:${s}`;

    // Dispatch & delivery dates
    const dispatchDay = new Date(cutoff);
    dispatchDay.setHours(0, 0, 0, 0);
    const deliveryDate  = getDeliveryDate(dispatchDay);
    const isToday       = toDateString(dispatchDay) === toDateString(now);

    if (dispatchDateEl) {
      dispatchDateEl.textContent = isToday
        ? '📦 Dispatched today'
        : `📦 Dispatched ${formatDate(dispatchDay)}`;
    }

    if (deliveryDateEl) {
      deliveryDateEl.textContent = `🚚 Estimated delivery: ${formatDate(deliveryDate)}`;
    }

    // Message
    if (messageEl) {
      const competitorNote = SHOW_COMPETITOR_NOTE
        ? ` — <em>${COMPETITOR_TEXT}</em>`
        : '';

      if (isToday) {
        messageEl.innerHTML =
          `Order in <em>${parseInt(h, 10)}h ${parseInt(m, 10)}m</em> for same-day dispatch${competitorNote}`;
      } else {
        messageEl.innerHTML =
          `Order by <em>4:45pm ${formatDate(dispatchDay)}</em> for next working day dispatch.`;
      }
    }

    applyUrgency(timeDiff);
  }

  // ── Initialise ───────────────────────────────────────────────────────────────
  updateCountdown();
  setInterval(updateCountdown, 1000);

})();
